import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// 合并 tailwind 类名
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 格式化时间
export function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  // 小于1分钟
  if (diff < 60000) {
    return '刚刚';
  }
  
  // 小于1小时
  if (diff < 3600000) {
    return `${Math.floor(diff / 60000)} 分钟前`;
  }
  
  // 小于24小时
  if (diff < 86400000) {
    return `${Math.floor(diff / 3600000)} 小时前`;
  }
  
  // 小于7天
  if (diff < 604800000) {
    return `${Math.floor(diff / 86400000)} 天前`;
  }
  
  // 超过7天显示日期
  return date.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
  });
}

// 格式化数字
export function formatNumber(num: number): string {
  if (num >= 100000000) {
    return (num / 100000000).toFixed(1) + '亿';
  }
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + '万';
  }
  return num.toLocaleString();
}

// 截断文本
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

// 生成唯一ID
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

// 复制到剪贴板
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
}

// 延迟函数
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// 防抖函数
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

// 从文本中提取视频ID
export function extractVideoId(text: string): string | null {
  // 匹配纯数字ID
  const idMatch = text.match(/(\d{10,})/);
  if (idMatch) return idMatch[1];
  
  // 从URL中提取
  const urlMatch = text.match(/\/video\/(\d+)/);
  if (urlMatch) return urlMatch[1];
  
  return null;
}

// 从文本中提取URL
export function extractUrl(text: string): string | null {
  const urlMatch = text.match(/(https?:\/\/\S+)/);
  return urlMatch ? urlMatch[1] : null;
}

// 从文本中提取关键词
export function extractKeyword(text: string): string | null {
  // 匹配引号中的内容
  const quoteMatch = text.match(/["']([^"']+)["']/);
  if (quoteMatch) return quoteMatch[1];
  
  // 匹配"关键词"后面的内容
  const keywordMatch = text.match(/(?:关键词|搜索|关于)[:：]?\s*(.+?)(?:\s|$)/);
  if (keywordMatch) return keywordMatch[1].trim();
  
  return null;
}
